"use client"

import { useState, useEffect, useMemo, useRef } from 'react'
import { Command, ExternalLink, Plus, Calendar, Users, Settings, Bell, Palette } from 'lucide-react'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useClientes, useEmpleados } from '@/hooks/useDatos'
import { nombreCliente } from '@/hooks/useDatos'
import { toast } from 'sonner'

interface CommandItem {
  id: string
  label: string
  description?: string
  icon?: React.ReactNode
  shortcut?: string
  section: string
  action: () => void
  keywords: string[]
}

export function CommandPalette() {
  const { data: clientes = [] } = useClientes()
  const { data: empleados = [] } = useEmpleados()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const itemsRef = useRef<HTMLDivElement>(null)

  // Generar lista de comandos
  const commands = useMemo((): CommandItem[] => {
    const baseCommands: CommandItem[] = [
      // Navegación
      { id: 'nav-agenda', label: 'Ir a Agenda', description: 'Ver turnos de hoy', icon: <Calendar className="h-4 w-4" />, shortcut: 'G A', section: 'Navegación', action: () => window.location.href = '/', keywords: ['agenda', 'turnos', 'hoy', 'calendario'] },
      { id: 'nav-clientes', label: 'Ir a Clientes', description: 'Gestionar clientes', icon: <Users className="h-4 w-4" />, shortcut: 'G C', section: 'Navegación', action: () => window.location.href = '/clientes', keywords: ['clientes', 'pacientes', 'lista'] },
      { id: 'nav-ajustes', label: 'Ir a Ajustes', description: 'Configuración del salón', icon: <Settings className="h-4 w-4" />, shortcut: 'G S', section: 'Navegación', action: () => window.location.href = '/ajustes', keywords: ['ajustes', 'configuracion', 'settings'] },

      // Acciones rápidas
      { id: 'new-turno', label: 'Nuevo turno', description: 'Crear turno para hoy', icon: <Plus className="h-4 w-4" />, shortcut: 'N', section: 'Acciones', action: () => { /* se maneja en Agenda */ toast.info('Usa el botón + en la agenda') }, keywords: ['nuevo', 'crear', 'turno', 'cita', 'agendar'] },
      { id: 'new-cliente', label: 'Nuevo cliente', description: 'Registrar cliente', icon: <Plus className="h-4 w-4" />, shortcut: 'Shift+N', section: 'Acciones', action: () => window.location.href = '/clientes?new=true', keywords: ['nuevo', 'crear', 'cliente', 'paciente', 'registrar'] },

      // Utilidades
      { id: 'export-csv', label: 'Exportar agenda CSV', description: 'Descargar turnos de la vista actual', icon: <ExternalLink className="h-4 w-4" />, shortcut: 'E', section: 'Utilidades', action: () => { toast.info('Función en la agenda') }, keywords: ['exportar', 'csv', 'excel', 'descargar'] },
      { id: 'toggle-theme', label: 'Cambiar tema', description: 'Alternar claro/oscuro', icon: <Palette className="h-4 w-4" />, shortcut: 'T', section: 'Utilidades', action: () => { document.documentElement.dataset.theme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark' }, keywords: ['tema', 'theme', 'oscuro', 'claro', 'dark', 'light'] },
      { id: 'notifications', label: 'Ver notificaciones', description: 'Recordatorios pendientes', icon: <Bell className="h-4 w-4" />, shortcut: 'B', section: 'Utilidades', action: () => { toast.info('Ver badge en agenda') }, keywords: ['notificaciones', 'recordatorios', 'bell', 'campana'] },
    ]

    // Agregar clientes como comandos
    const clienteCommands: CommandItem[] = clientes.slice(0, 20).map(c => ({
      id: `cliente-${c.id}`,
      label: nombreCliente(c),
      description: c.whatsapp ? `📱 ${c.whatsapp}` : 'Sin WhatsApp',
      icon: <Users className="h-4 w-4" />,
      shortcut: '',
      section: 'Clientes',
      action: () => window.location.href = `/clientes/${c.id}`,
      keywords: [c.nombre.toLowerCase(), c.apellido?.toLowerCase() || '', c.referencia?.toLowerCase() || '', c.whatsapp || ''].filter(Boolean)
    }))

    // Agregar empleados
    const empleadoCommands: CommandItem[] = empleados.slice(0, 10).map(e => ({
      id: `empleado-${e.id}`,
      label: e.nombre,
      description: e.activo ? 'Activo' : 'Inactivo',
      icon: <Users className="h-4 w-4" />,
      shortcut: '',
      section: 'Empleados',
      action: () => window.location.href = `/empleados/${e.id}`,
      keywords: [e.nombre.toLowerCase()]
    }))

    return [...baseCommands, ...clienteCommands, ...empleadoCommands]
  }, [clientes, empleados])

  // Filtrar comandos
  const filteredCommands = useMemo(() => {
    if (!query.trim()) return commands
    const q = query.toLowerCase()
    return commands.filter(cmd =>
      cmd.label.toLowerCase().includes(q) ||
      cmd.description?.toLowerCase().includes(q) ||
      cmd.keywords.some(k => k.includes(q)) ||
      cmd.section.toLowerCase().includes(q)
    )
  }, [commands, query])

  // Agrupar por sección
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {}
    filteredCommands.forEach(cmd => {
      if (!groups[cmd.section]) groups[cmd.section] = []
      groups[cmd.section].push(cmd)
    })
    return groups
  }, [filteredCommands])

  // Abrir/cerrar con Cmd+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) o Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(!open)
      }
      // Escape para cerrar
      if (e.key === 'Escape' && open) {
        e.preventDefault()
        setOpen(false)
        setQuery('')
        setSelectedIndex(0)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  // Focus input al abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Navegación con teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const total = filteredCommands.length
    if (total === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => (i + 1) % total)
      scrollToSelected()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => (i - 1 + total) % total)
      scrollToSelected()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const cmd = filteredCommands[selectedIndex]
      if (cmd) {
        cmd.action()
        setOpen(false)
        setQuery('')
        setSelectedIndex(0)
      }
    }
  }

  const scrollToSelected = () => {
    const item = itemsRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    item?.scrollIntoView({ block: 'nearest' })
  }

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[70vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-2 p-3 border-b">
            <Command className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium">Buscar comandos</span>
            <kbd className="ml-auto px-2 py-0.5 text-xs bg-muted rounded">⌘K</kbd>
          </div>

          {/* Input */}
          <div className="p-3 border-b">
            <Input
              ref={inputRef}
              placeholder="Escribe para buscar..."
              value={query}
              onChange={e => { setQuery(e.target.value); setSelectedIndex(0) }}
              onKeyDown={handleKeyDown}
              className="w-full"
              autoFocus
            />
          </div>

          {/* Resultados */}
          <div ref={itemsRef} className="flex-1 overflow-y-auto p-2">
            {Object.entries(groupedCommands).map(([section, items]) => (
              <div key={section} className="mb-2">
                <h3 className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {section}
                </h3>
                {items.map((cmd) => {
                  const globalIndex = filteredCommands.indexOf(cmd)
                  const isSelected = globalIndex === selectedIndex
                  return (
                    <button
                      key={cmd.id}
                      data-index={globalIndex}
                      onClick={cmd.action}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors',
                        isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      )}
                      style={{ outline: isSelected ? '2px solid currentColor' : 'none' }}
                    >
                      {cmd.icon && <span className="flex-shrink-0">{cmd.icon}</span>}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{cmd.label}</p>
                        {cmd.description && <p className="text-xs opacity-70 truncate">{cmd.description}</p>}
                      </div>
                      {cmd.shortcut && (
                        <kbd className="flex-shrink-0 px-2 py-0.5 text-xs bg-muted/50 rounded font-mono opacity-70">
                          {cmd.shortcut}
                        </kbd>
                      )}
                    </button>
                  )
                })}
              </div>
            ))}

            {filteredCommands.length === 0 && query && (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <p>No se encontraron comandos para "{query}"</p>
              </div>
            )}

            {filteredCommands.length === 0 && !query && (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <p className="text-center">Presiona ⌘K para buscar • {commands.length} comandos disponibles</p>
              </div>
            )}
          </div>

          {/* Footer ayuda */}
          <div className="p-2 border-t text-xs text-muted-foreground flex items-center justify-between">
            <span>↑↓ Navegar • Enter Ejecutar • Esc Cerrar</span>
            <span>⌘K para cerrar</span>
          </div>
        </DialogContent>
    </Dialog>
  )
}

// Trigger invisible para atajo global
export function CommandPaletteTrigger() {
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="hidden" onClick={() => setOpen(true)}>Abrir paleta</button>
      </DialogTrigger>
      <CommandPalette />
    </Dialog>
  )
}